import React, { useEffect, useState } from "react";
import "./curated-stack.scss";
import { getRingImageUrls } from "../../utils/ring-image.utils";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { useNavigate } from "react-router-dom";

import BasePage from "../../components/base-page/base-page";
import RingSwipe from "../../components/ring-swipe/ring-swipe";
import Spinner from "../../components/spinner/spinner";
import ringFallbackImage from "../../assets/ring_image.png";
import { selectCurrentUser } from "../../redux/user/user.selectors";
import {
  fetchCuratedCandidateRings,
  fetchRingsByIds,
  getCuratedStackForUser,
  markCuratedCampaignClick,
  setCuratedStackForUser,
} from "../../utils/firebase.utils";
import { ICurrentUser, IRing } from "../../utils/interfaces";
import {
  buildCuratedHistoryProfile,
  buildWhyThisFitsNote,
  CURATED_BAND_PERSONALITY_OPTIONS,
  CURATED_COLOR_MOOD_OPTIONS,
  CURATED_EXTRA_LEVEL_OPTIONS,
  CURATED_METAL_OPTIONS,
  CURATED_SHAPE_OPTIONS,
  CURATED_SILHOUETTE_OPTIONS,
  CURATED_SIZE_VIBE_OPTIONS,
  CURATED_STONE_ENERGY_OPTIONS,
  CuratedStackQuiz,
  CuratedStackRecordLocal,
  DEFAULT_CURATED_QUIZ,
  generateCuratedStackIds,
  getLocalCuratedStack,
  isCuratedStackExpired,
  clearCuratedOnboardingPending,
  setLocalCuratedStack,
} from "../../utils/curated-stack.utils";

type CuratedStackPageProps = {
  currentUser: ICurrentUser | null;
};

type QuizStep = {
  key: keyof CuratedStackQuiz;
  label: string;
  options: { value: string; label: string }[];
  maxSelect?: number;
};

const asTags = (ring: IRing) =>
  Array.isArray(ring.stoneCut) ? ring.stoneCut : ring.stoneCut ? [ring.stoneCut] : [];

const getRingPrimaryUrl = (ring: IRing) => {
  const url = getRingImageUrls(ring)[0];
  return url || ringFallbackImage;
};

const extractRingIdsFromHistory = (entries?: Array<string | { id?: string }>) => {
  if (!Array.isArray(entries)) return [];
  return entries
    .map((entry) => (typeof entry === "string" ? entry : entry?.id))
    .filter((id): id is string => Boolean(id));
};

const getInteractionCount = (user: ICurrentUser | null) => {
  if (!user) return 0;
  return extractRingIdsFromHistory(user.likedRings as Array<string | { id?: string }>).length +
    extractRingIdsFromHistory(user.dislikedRings as Array<string | { id?: string }>).length;
};

const CuratedStackPage = ({ currentUser }: CuratedStackPageProps) => {
  const navigate = useNavigate();

  const [quiz, setQuiz] = useState<CuratedStackQuiz>(DEFAULT_CURATED_QUIZ);
  const [stackRecord, setStackRecord] = useState<CuratedStackRecordLocal | null>(null);
  const [rings, setRings] = useState<IRing[]>([]);
  const [quizStep, setQuizStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPreparingOptions, setIsPreparingOptions] = useState(false);
  const [candidateRings, setCandidateRings] = useState<IRing[]>([]);
  const [error, setError] = useState<string | null>(null);

  const quizSteps: QuizStep[] = [
    { key: "stoneColorMood", label: "Pick your color mood", options: CURATED_COLOR_MOOD_OPTIONS },
    { key: "stoneEnergy", label: "What center stone energy do you want?", options: CURATED_STONE_ENERGY_OPTIONS },
    {
      key: "silhouetteVibes",
      label: "What overall silhouette feels most like you? (pick up to 2)",
      options: CURATED_SILHOUETTE_OPTIONS,
      maxSelect: 2,
    },
    {
      key: "shapePreferences",
      label: "Pick your favorite stone shapes (up to 2)",
      options: [...CURATED_SHAPE_OPTIONS],
      maxSelect: 2,
    },
    { key: "extraLevel", label: "How extra should it be?", options: CURATED_EXTRA_LEVEL_OPTIONS },
    { key: "bandPersonality", label: "Band personality", options: CURATED_BAND_PERSONALITY_OPTIONS },
    { key: "metalTone", label: "Metal tone on your hand", options: CURATED_METAL_OPTIONS },
    { key: "sizeVibe", label: "Stone size vibe", options: CURATED_SIZE_VIBE_OPTIONS },
  ];

  const ensureCandidateRings = async (): Promise<IRing[]> => {
    if (candidateRings.length > 0) return candidateRings;

    setIsPreparingOptions(true);
    try {
      const allRings = await fetchCuratedCandidateRings(260);
      setCandidateRings(allRings);
      return allRings;
    } finally {
      setIsPreparingOptions(false);
    }
  };

  const findPreviewImageForOption = (key: keyof CuratedStackQuiz, value: string) => {
    const normalizedValue = value.toLowerCase().replace(/[^a-z0-9]/g, "");

    const pool = candidateRings.filter((ring) => {
      const tags = asTags(ring).map((tag) => tag.toLowerCase().replace(/[^a-z0-9]/g, ""));
      return tags.some((tag) => tag.includes(normalizedValue) || normalizedValue.includes(tag));
    });

    const preferredRing =
      pool.find((ring) => getRingPrimaryUrl(ring)) || candidateRings.find((ring) => getRingPrimaryUrl(ring));
    if (!preferredRing) return ringFallbackImage;

    return getRingPrimaryUrl(preferredRing);
  };

  const buildOptionCardBackground = (key: keyof CuratedStackQuiz, value: string) => {
    const generatedImagePath = `/curated-quiz/${key}-${value}.svg`;
    const fallbackImage = findPreviewImageForOption(key, value);
    return `url(${generatedImagePath}), url(${fallbackImage})`;
  };

  const hydrateRingsForStack = async (record: CuratedStackRecordLocal): Promise<IRing[]> => {
    const fetchedRings = await fetchRingsByIds(record.ringIds);

    const ringsById = new Map(fetchedRings.map((ring) => [ring.id, ring]));
    const sortedRings = record.ringIds
      .map((ringId) => ringsById.get(ringId))
      .filter((ring): ring is IRing => Boolean(ring));

    if (sortedRings.length === 0) {
      throw new Error("No rings found for curated stack ids");
    }

    setRings(sortedRings);
    return sortedRings;
  };

  useEffect(() => {
    clearCuratedOnboardingPending();

    const resolveStack = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const toLocalRecord = (record: any): CuratedStackRecordLocal | null => {
          if (!record?.quiz || !Array.isArray(record?.ringIds)) return null;
          return {
            quiz: record.quiz,
            ringIds: record.ringIds,
            generatedAt: record.generatedAt,
          };
        };

        if (currentUser?.id) {
          const firestoreStack = toLocalRecord(await getCuratedStackForUser(currentUser.id));

          if (firestoreStack) {
            setQuiz(firestoreStack.quiz);
            if (!isCuratedStackExpired(firestoreStack.generatedAt)) {
              try {
                await hydrateRingsForStack(firestoreStack);
                setStackRecord(firestoreStack);
              } catch {
                await generateStack(firestoreStack.quiz);
              }
            } else {
              await generateStack(firestoreStack.quiz);
            }
          } else {
            const interactions = getInteractionCount(currentUser);
            if (interactions >= 30 && interactions < 2000) {
              await generateStack(quiz);
            } else {
              setStackRecord(null);
              setRings([]);
            }
          }
        } else {
          const localStack = getLocalCuratedStack();

          if (localStack) {
            setQuiz(localStack.quiz);
            if (!isCuratedStackExpired(localStack.generatedAt)) {
              try {
                await hydrateRingsForStack(localStack);
                setStackRecord(localStack);
              } catch {
                await generateStack(localStack.quiz);
              }
            } else {
              await generateStack(localStack.quiz);
            }
          } else {
            setStackRecord(null);
            setRings([]);
          }
        }
      } catch (stackError) {
        console.error(stackError);
        setError("Could not load your curated stack right now.");
      } finally {
        setIsLoading(false);
      }
    };

    resolveStack();
  }, [currentUser?.id]);

  useEffect(() => {
    if (!stackRecord) {
      ensureCandidateRings().catch((prepError) => {
        console.error(prepError);
      });
    }
  }, [stackRecord]);

  useEffect(() => {
    if (!currentUser?.id) return;
    markCuratedCampaignClick(currentUser.id).catch((error) => console.error(error));
  }, [currentUser?.id]);

  const isOptionSelected = (step: QuizStep, value: string) => {
    const current = quiz[step.key] as string | string[];
    return Array.isArray(current) ? current.includes(value) : current === value;
  };

  const updateQuizValue = (step: QuizStep, value: string) => {
    setQuiz((previous) => {
      const key = step.key;
      const current = previous[key] as string | string[];

      if (step.maxSelect) {
        const selected = Array.isArray(current) ? [...current] : [];
        if (selected.includes(value)) {
          return {
            ...previous,
            [key]: selected.filter((item) => item !== value),
          };
        }

        if (selected.length >= step.maxSelect) {
          selected.shift();
        }

        return {
          ...previous,
          [key]: [...selected, value],
        };
      }

      return {
        ...previous,
        [key]: value,
      };
    });
  };

  const isCurrentStepAnswered = () => {
    const step = quizSteps[quizStep];
    const value = quiz[step.key] as string | string[];

    if (Array.isArray(value)) return value.length > 0;
    return Boolean(value);
  };

  const generateStack = async (quizToUse: CuratedStackQuiz) => {
    setIsGenerating(true);
    setError(null);

    try {
      const allRings = await ensureCandidateRings();

      let historyProfile;
      if (currentUser) {
        const likedIds = extractRingIdsFromHistory(currentUser.likedRings as Array<string | { id?: string }>).slice(-120);
        const dislikedIds = extractRingIdsFromHistory(currentUser.dislikedRings as Array<string | { id?: string }>).slice(-120);

        if (likedIds.length || dislikedIds.length) {
          const [likedRings, dislikedRings] = await Promise.all([
            likedIds.length ? fetchRingsByIds(Array.from(new Set(likedIds))) : Promise.resolve([]),
            dislikedIds.length ? fetchRingsByIds(Array.from(new Set(dislikedIds))) : Promise.resolve([]),
          ]);
          historyProfile = buildCuratedHistoryProfile(likedRings, dislikedRings);
        }
      }

      const generatedIds = generateCuratedStackIds(allRings, quizToUse, 10, historyProfile);

      if (generatedIds.length === 0) {
        throw new Error("No rings available");
      }

      const record: CuratedStackRecordLocal = {
        quiz: quizToUse,
        ringIds: generatedIds,
        generatedAt: Date.now(),
      };

      if (currentUser?.id) {
        await setCuratedStackForUser(currentUser.id, record);
      } else {
        setLocalCuratedStack(record);
      }

      await hydrateRingsForStack(record);
      setStackRecord(record);
      setQuizStep(0);
    } catch (generationError) {
      console.error(generationError);
      setError("Could not generate your stack. Please try again.");
      setStackRecord(null);
      setRings([]);
    } finally {
      setIsGenerating(false);
    }
  };

  const restartQuiz = () => {
    setStackRecord(null);
    setRings([]);
    setQuizStep(0);
  };

  return (
    <BasePage>
      <div className="curated-stack">
        <section className="curated-stack__hero">
          <p className="curated-stack__eyebrow">Personalized in under a minute</p>
          <h1>Find your best 10-ring stack</h1>
          <p className="curated-stack__intro">
            Answer quick style questions and we will pick your strongest matches for today.
          </p>
        </section>

        {(isLoading || isGenerating || (!stackRecord && isPreparingOptions)) && <Spinner theme="dark" />}

        {error && <p className="curated-stack__error">{error}</p>}

        {!isLoading && !isGenerating && !stackRecord && (
          <section className="curated-stack__quiz">
            <div className="curated-stack__step-row">
              <p className="curated-stack__step">Step {quizStep + 1} of {quizSteps.length}</p>
              <div className="curated-stack__progress-track" aria-hidden>
                <span
                  className="curated-stack__progress-fill"
                  style={{ width: `${((quizStep + 1) / quizSteps.length) * 100}%` }}
                />
              </div>
            </div>

            <h2>{quizSteps[quizStep].label}</h2>
            <div className="curated-stack__option-swipe-list">
              {quizSteps[quizStep].options.map((option) => (
                <button
                  key={option.value}
                  className={`curated-stack__option-card ${isOptionSelected(quizSteps[quizStep], option.value) ? "active" : ""}`}
                  onClick={() => updateQuizValue(quizSteps[quizStep], option.value)}
                  style={{ backgroundImage: buildOptionCardBackground(quizSteps[quizStep].key, option.value) }}
                >
                  <span>{option.label}</span>
                </button>
              ))}
            </div>

            <div className="curated-stack__quiz-actions">
              <button
                className="secondary"
                onClick={() => setQuizStep((previous) => Math.max(previous - 1, 0))}
                disabled={quizStep === 0}
              >
                Back
              </button>

              {quizStep < quizSteps.length - 1 ? (
                <button
                  className="primary"
                  onClick={() => setQuizStep((previous) => Math.min(previous + 1, quizSteps.length - 1))}
                  disabled={!isCurrentStepAnswered()}
                >
                  Next
                </button>
              ) : (
                <button className="primary" onClick={() => generateStack(quiz)} disabled={!isCurrentStepAnswered()}>
                  Generate today's stack
                </button>
              )}
            </div>
          </section>
        )}

        {!isLoading && !isGenerating && stackRecord && (
          <>
            <div className="curated-stack__deck-header">
              <div>
                <h2>Your Curated Stack</h2>
                <p>{new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</p>
              </div>
              <button className="secondary" onClick={restartQuiz}>Retake</button>
            </div>

            <section className="curated-stack__results" aria-label="Curated swipe deck">
              <div className="curated-stack__results-head">
                <h2>Your 10-ring stack</h2>
                <p>Swipe right to like, left to skip. This is what feeds your preference data.</p>
              </div>

              <div className="curated-stack__swipe-container">
                <RingSwipe
                  NoMoreRingsToFetch={true}
                  ringsFromDb={rings}
                  setAllFiltersToTrue={function (): void {}}
                  fetchRings={function (): void {}}
                  curatedMode={true}
                  curatedReasons={(ring) => {
                    const reasons = buildWhyThisFitsNote(ring, quiz);
                    return reasons.length ? reasons : ["Strong overall match to your style profile"];
                  }}
                />
              </div>
            </section>
          </>
        )}

        {!isLoading && !isGenerating && stackRecord && (
          <section className="curated-stack__actions">
            <button
              className="primary"
              onClick={() => navigate(currentUser ? "/liked-rings" : "/login")}
            >
              Continue to saved rings
            </button>
            <button className="secondary" onClick={restartQuiz}>
              Rebuild stack
            </button>
          </section>
        )}
      </div>
    </BasePage>
  );
};

const mapStateToProps = createStructuredSelector({
  currentUser: selectCurrentUser,
});

export default connect(mapStateToProps)(CuratedStackPage);
